import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/format";

const FIELDS = ["title", "description", "category", "frequency", "frequency_text", "deadline_text", "penalty_text", "next_due", "lead_days", "order_priority", "page"];
const toForm = (o) => ({ ...Object.fromEntries(FIELDS.map((k) => [k, o[k] ?? ""])), next_due: o.next_due ? o.next_due.slice(0, 10) : "" });

const Decisions = ({ o, run }) => {
  const b = (d, label, variant = "default") => <Button key={d} variant={variant} onClick={() => run(d)} className="rounded-xl" data-testid={`obl-decision-${d}`}>{label}</Button>;
  if (o.status === "bozza") return [b("convalida", "Convalida e attiva"), b("scarta", "Scarta", "destructive")];
  if (o.status === "attivo") return [b("adempiuto", "Segna adempiuto", "outline"), b("sospendi", "Riporta in bozza", "outline")];
  if (o.status === "adempiuto") return [b("riattiva", "Riattiva", "outline")];
  return null;
};

export const ObligationDialog = ({ ob, frequencies, onClose, onSaved }) => {
  const [o, setO] = useState(null);
  const [f, setF] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => {
    api.get(`/obligations/${ob.id}`).then((r) => {
      setO(r.data);
      setF(toForm(r.data));
    });
  }, [ob.id]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    try {
      const { data } = await api.put(`/obligations/${o.id}`, { ...f, lead_days: Number(f.lead_days) || 0, page: f.page ? Number(f.page) : null,
        next_due: f.next_due ? new Date(`${f.next_due}T09:00:00`).toISOString() : null, version: o.version });
      setO({ ...o, ...data });
      toast.success("Obbligo aggiornato");
      onSaved();
      return data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    }
  };
  const run = async (decision) => {
    if (o.can_manage && o.status !== "scartato" && JSON.stringify(f) !== JSON.stringify(toForm(o)) && !(await save())) return;
    try {
      await api.post(`/obligations/${o.id}/decision`, { decision, note });
      toast.success("Fatto");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const txt = (k, label, props = {}) => <div className="space-y-1"><Label>{label}</Label><Input value={f[k]} onChange={(e) => set(k, e.target.value)} disabled={!o.can_manage} data-testid={`obl-form-${k}`} {...props} /></div>;
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-2xl" data-testid="obl-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">{ob.code} · {ob.status_label}</DialogTitle></DialogHeader>
        {!o || !f ? <p>Caricamento…</p> : (
          <div className="space-y-4">
            {o.doc_id && <Link to={`/documenti/${o.doc_id}?page=${o.page}`} className="block rounded-xl bg-muted p-3 text-sm" data-testid="obl-source"><b>Fonte: {o.doc_title}, p. {o.page}</b>{o.quote && <span className="mt-1 block italic text-muted-foreground">«{o.quote}»</span>}</Link>}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">{txt("title", "Titolo")}</div>
              <div className="space-y-1 sm:col-span-2"><Label>Descrizione</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} disabled={!o.can_manage} data-testid="obl-form-description" /></div>
              <div className="space-y-1"><Label>Frequenza</Label><NativeSelect value={f.frequency} onChange={(v) => set("frequency", v)} disabled={!o.can_manage} className="w-full" testid="obl-form-frequency">{Object.entries(frequencies).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect></div>
              {txt("next_due", "Prossima scadenza", { type: "date" })}
              {txt("frequency_text", "Frequenza nel testo")}{txt("deadline_text", "Termine (es. entro 24 ore)")}
              <div className="sm:col-span-2">{txt("penalty_text", "Penale")}</div>
              {txt("lead_days", "Proponi l'ordine con giorni di anticipo", { type: "number", min: 0, max: 90 })}
              <div className="space-y-1"><Label>Priorità ordine</Label><NativeSelect value={f.order_priority} onChange={(v) => set("order_priority", v)} disabled={!o.can_manage} className="w-full" testid="obl-form-priority">{["normale", "alta"].map((p) => <option key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</option>)}</NativeSelect></div>
            </div>
            {o.orders.length > 0 && <div className="text-sm"><b>Ordini collegati</b>{o.orders.map((x) => <Link key={x.id} to={`/ordini/${x.id}`} className="block text-primary">{x.code} · {x.title} · {x.status}</Link>)}</div>}
            {o.periods?.length > 0 && <div className="text-xs text-muted-foreground">{o.periods.map((p) => <p key={p.period}>{fmtDT(p.period)} · {p.esito}</p>)}</div>}
            {o.validated_by_name && <p className="text-xs text-muted-foreground">Convalidato da {o.validated_by_name} il {fmtDT(o.validated_at)}</p>}
            {o.can_manage && (
              <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:flex-wrap">
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (obbligatoria per scartare)" className="rounded-xl sm:flex-1" data-testid="obl-decision-note" />
                {o.status !== "scartato" && <Button variant="outline" onClick={save} className="rounded-xl" data-testid="obl-save">Salva</Button>}
                <Decisions o={o} run={run} />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
